import { Utils } from 'common';

const BaseCommunication = Utils.Communication;

class AccountCommunication extends BaseCommunication {
	static init(params) {
		BaseCommunication.init(params);
	}

	static getCancelUrlFromRetention(data) {
		return this.axiosRequest({
			method: 'post',
			url: `https://app.retention.chargebee.com/precancel`,
			data,
		});
	}

	static getMaxPostageBalance(custId) {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/account/maxpostagebalance/${custId}`,
		});
	}

	static getCancelAccountInfo() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/CloseAccount/info`,
		});
	}

	static saveCancelAccountInfo(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/CloseAccount/info`,
			data,
		});
	}

	static waiveServiceFees(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/CloseAccount/waiveFees`,
			data,
		});
	}

	static closeAccount(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/CloseAccount`,
			data,
			timeout: 60000, // WEBAPPS-26083 to give time for both SWSIM and Shipstation close account to happen. Shiptation call has a timeout limit of 25s in the backend.
		});
	}

	static setClickToCall(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/CloseAccount/clicktocall`,
			data,
		});
	}

	static changePassword(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/account/changePassword`,
			data,
		});
	}

	static getCommPrefs() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/account/communicationPreference`,
		});
	}

	static setCommPrefs(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/account/communicationPreference`,
			data,
		});
	}

	static getReferAFriendData() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/account/referafriend`,
		});
	}

	static getCorporationContactInfo() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/account/CorporationContacts`,
		});
	}

	static updateCorporationContactInfo(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/account/CorporationContacts`,
			data,
		});
	}

	static closeAccountEligible() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/CloseAccount/Eligible`,
		});
	}

	static async getLicenseInformation(custId) {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/account/LicenseInformation/${custId}`,
		});
	}

	static async updateLicenseInformation(data, custId) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/account/LicenseInformation/${custId}`,
			data,
		});
	}

	static async setAllocatedFund(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/payment/setAllocatedFund`,
			data,
		});
	}

	static getLocations() {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/location`,
			timeout: 180000,
		});
	}

	static getFullLocations() {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/location/full`,
			timeout: 180000,
		});
	}

	static updateLocations(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/location`,
			data,
		});
	}

	static appendAccountInfoToLocations(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/location/appendAccountInfo`,
			data,
		});
	}

	static getRegions() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/region`,
		});
	}

	static addRegion(regionName) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/region/${regionName}`,
		});
	}

	static updateRegion({ regionId, regionName }) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/region/${regionId}/${regionName}`,
		});
	}

	static deleteRegions(data) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/region`,
			data,
		});
	}

	static async getPaymentAccounts(custId = 0) {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/payment/${custId}`,
		});
	}

	static async createPaymentAccount(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/payment`,
			data,
		});
	}

	static async updatePaymentAccount(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/payment`,
			data,
		});
	}

	static async associatePaymentAccountForChargeCategory(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/payment/associate/${data.CustId}`,
			data,
		});
	}

	static async deletePaymentAccount(PaymentAccountId) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/payment/${PaymentAccountId}`,
		});
	}

	static async updatePaymentNickname(PaymentAccountId, NickName, CustId) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/payment/${PaymentAccountId}/${NickName}/${CustId}`,
		});
	}

	static async getCorporationPaymentAccounts() {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/payment/corp`,
		});
	}

	static async createCorporationPaymentAccount(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/payment/corp`,
			data,
		});
	}

	static getMaxAllowedUsers() {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/user/maxallowed`,
		});
	}

	// Backed by SERA AccountManagement_SetMaximumAllowedUsers.
	// Payload: { NewMaximumAllowedUsers: number } — CustId/AdminUserId are
	// injected proxy-side from the authenticated session.
	static setMaximumAllowedUsers(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/user/maxallowed`,
			data,
		});
	}

	static addUser(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/user`,
			data,
			timeout: 180000,
		});
	}

	static async updateCorporationPaymentAccount(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/payment/corp`,
			data,
		});
	}

	static async associateCorporationPaymentAccountForChargeCategory(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/payment/corp/associate`,
			data,
		});
	}

	static updateUser(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/user`,
			data,
		});
	}

	static updateMultipleUser(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/user/capabilities`,
			data,
		});
	}

	static async deleteCorporationPaymentAccount(PaymentAccountId) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/payment/corp/${PaymentAccountId}`,
		});
	}

	static async updateCorporationPaymentNickname(PaymentAccountId, NickName) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/payment/corp/${PaymentAccountId}/${NickName}`,
		});
	}

	static deleteUsers(data) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/user`,
			data,
		});
	}

	static resetPassword(userId) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/user/ResetPassword/${userId}`,
		});
	}

	static getActivationUrl(activatingUserId) {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/user/ActivationUrl/${activatingUserId}`,
		});
	}

	static sendActivationUrl(activatingUserId) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/user/ActivationUrl/${activatingUserId}`,
		});
	}

	static getUserForActivation({ stampsId, activationCode, messageCode }) {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/misc/Activate/${stampsId}/${activationCode}/${messageCode}`,
		});
	}

	static activateUser(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/misc/Activate/${data.StampsId}/${data.ActivationCode}/${data.MessageCode}`,
			data,
		});
	}

	static getCodewordQuestions(userName) {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/auth/codewords/${userName}`,
		});
	}

	static getCorpUsers(custId = 0) {
		return this.axiosRequest({
			url: `${this.accountApiUrlPrefix}/user/corp/${custId}`,
		});
	}

	static addCorpUser(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/user/corp`,
			data,
			timeout: 180000,
		});
	}

	static updateCorpUser(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/user/corp`,
			data,
		});
	}

	static deleteCorpUsers(data) {
		return this.axiosRequest({
			method: 'delete',
			url: `${this.accountApiUrlPrefix}/user/corp`,
			data,
		});
	}

	static getUserCorpInfo(targetUserid = 0) {
		return this.axiosRequest({
			method: 'get',
			url: `${this.accountApiUrlPrefix}/user/corp/info/${targetUserid}`,
		});
	}

	static updateMultipleCorpUser(data) {
		return this.axiosRequest({
			method: 'put',
			url: `${this.accountApiUrlPrefix}/user/corp/capabilities`,
			data,
		});
	}

	static getAutoRefundOptOut(custId = 0, data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/r/sera/internal/v1/prints/autorefundoptout/${custId}`,
			data,
		});
	}

	static autoRefundOptOut(data) {
		return this.axiosRequest({
			method: 'post',
			url: `${this.accountApiUrlPrefix}/r/sera/v1/autorefundoptout`,
			data,
		});
	}
}

export default AccountCommunication;
